import { useState } from "react";
import { 
  Heart, 
  Home, 
  Users, 
  Briefcase, 
  MessageCircle,
  HandHeart,
  Shield,
  Sparkles,
  ChevronDown,
  ChevronUp
} from "lucide-react";

const sampleMessages = [
  {
    id: "romantic-angry",
    category: "Romantic Relationship",
    emotion: "Angry",
    icon: Heart,
    color: "text-rose-500",
    bgColor: "bg-rose-50",
    message: "I honestly don't understand why you keep doing this. You say you care, but your actions show the opposite. I waited around all night while you couldn't even send a quick update. It feels like my time doesn't matter to you at all, and I'm tired of always being the one chasing you for basic respect."
  },
  {
    id: "romantic-hurt",
    category: "Romantic Relationship",
    emotion: "Hurt",
    icon: Heart,
    color: "text-rose-500",
    bgColor: "bg-rose-50",
    message: "I know you probably didn't mean to hurt me, but what you said really stuck with me. I've been replaying it in my head all day and it made me feel like I don't matter as much as I thought I did."
  },
  {
    id: "dating-frustrated",
    category: "Dating",
    emotion: "Frustrated",
    icon: Heart,
    color: "text-pink-500",
    bgColor: "bg-pink-50",
    message: "It's confusing when you disappear for days and then act like everything is normal. I don't know where I stand with you, and honestly it's exhausting."
  },
  {
    id: "family-tension",
    category: "Family",
    emotion: "Tension",
    icon: Home,
    color: "text-amber-600",
    bgColor: "bg-amber-50",
    message: "I'm tired of feeling judged every time we talk. It seems like nothing I do is ever good enough, and it makes me not want to share anything with you."
  },
  {
    id: "coparenting-conflict",
    category: "Co-Parenting",
    emotion: "Conflict",
    icon: Home,
    color: "text-orange-500",
    bgColor: "bg-orange-50",
    message: "You can't just change plans last minute and expect me to rearrange everything. I have responsibilities too, and this puts me in a really difficult position."
  },
  {
    id: "workplace-frustration",
    category: "Workplace",
    emotion: "Professional Frustration",
    icon: Briefcase,
    color: "text-slate-600",
    bgColor: "bg-slate-50",
    message: "I've asked for this information several times and still haven't received it. The delay is affecting my ability to finish the project, and it's becoming a serious issue."
  },
  {
    id: "friendship-disappointment",
    category: "Friendship",
    emotion: "Disappointment",
    icon: Users,
    color: "text-sky-500",
    bgColor: "bg-sky-50",
    message: "I was really counting on you to be there, and when you didn't show up or even let me know, it honestly hurt more than I expected."
  },
  {
    id: "apology-needed",
    category: "Apology Needed",
    emotion: "Regretful",
    icon: HandHeart,
    color: "text-violet-500",
    bgColor: "bg-violet-50",
    message: "I know I messed up and probably made things worse by reacting the way I did. I've been thinking about it and I regret how I handled the situation."
  },
  {
    id: "boundary-setting",
    category: "Boundary Setting",
    emotion: "Firm",
    icon: Shield,
    color: "text-emerald-600",
    bgColor: "bg-emerald-50",
    message: "I care about you, but I can't keep having the same argument over and over. It's draining, and I need things to change if we're going to move forward."
  },
  {
    id: "light-misunderstanding",
    category: "Light Misunderstanding",
    emotion: "Clarifying",
    icon: MessageCircle,
    color: "text-teal-500",
    bgColor: "bg-teal-50",
    message: "I think we might be talking past each other. That wasn't what I meant at all, and I don't want this to turn into something bigger than it needs to be."
  }
];

export default function SampleMessages({ onSelectSample }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const displayedSamples = isExpanded ? sampleMessages : sampleMessages.slice(0, 4);

  return (
    <div className="w-full" data-testid="sample-messages">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="text-[#2F5BFF]" size={18} />
        <h3 className="text-base font-semibold text-[#1F2937]">
          Try an Example Message
        </h3>
      </div>
      <p className="text-sm text-[#6B7280] mb-4">
        Tap any example to paste it into the message box.
      </p>

      {/* Sample List */}
      <div className="space-y-2">
        {displayedSamples.map((sample) => {
          const Icon = sample.icon;
          return (
            <button
              key={sample.id}
              onClick={() => onSelectSample(sample.message)}
              data-testid={`sample-${sample.id}`}
              className="w-full text-left p-4 rounded-xl border border-[#E5E7EB] bg-white
                         hover:border-[#2F5BFF]/30 hover:bg-[#F7F9FC] 
                         active:scale-[0.99] transition-all duration-200 group"
            >
              {/* Category & Emotion Label */}
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-6 h-6 rounded-lg ${sample.bgColor} flex items-center justify-center`}>
                  <Icon size={14} className={sample.color} />
                </div>
                <span className="text-xs font-semibold text-[#1F2937] uppercase tracking-wide">
                  {sample.category}
                </span>
                <span className="text-xs text-[#6B7280]">•</span>
                <span className="text-xs text-[#6B7280] capitalize">
                  {sample.emotion}
                </span>
              </div>
              
              {/* Message Preview */}
              <p className="text-sm text-[#4B5563] leading-relaxed line-clamp-2 group-hover:text-[#1F2937] transition-colors">
                "{sample.message}"
              </p>
            </button>
          );
        })}
      </div>

      {/* Show More/Less Button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full mt-3 py-3 text-sm font-medium text-[#2F5BFF] hover:text-[#1E40AF]
                   flex items-center justify-center gap-1 transition-colors"
        data-testid="toggle-samples"
      >
        {isExpanded ? (
          <>
            Show Less <ChevronUp size={16} />
          </>
        ) : (
          <>
            Show {sampleMessages.length - 4} More Examples <ChevronDown size={16} />
          </>
        )}
      </button>
    </div>
  );
}

export { sampleMessages };
